import { useCallback, useState } from "react";
import {
  STORAGE_KEYS,
  PREVIEW_DATA_EXPIRY_MS,
} from "../constants";
import {
  StravaActivitiesData,
  StravaActivitiesListResponse,
  StravaActivitySummary,
  StravaActivitySummaryAPI,
} from "../types";
import { DateFilterOption } from "BEDROCK/shared-components/filter-chips";

/**
 * Hook for managing Strava activities list data in localStorage.
 *
 * This allows users to browse and select from their recent Strava activities
 * after OAuth callback. The data persists for 6 hours (matches Strava access token lifetime).
 */
export const useStravaActivities = () => {
  /**
   * Save activities data to localStorage.
   *
   * @param data - The Strava activities data to save
   */
  const saveActivitiesData = useCallback((data: StravaActivitiesData): void => {
    localStorage.setItem(STORAGE_KEYS.ACTIVITIES_DATA, JSON.stringify(data));
  }, []);

  /**
   * Load activities data from localStorage.
   * Returns null if not found or expired (>6 hours old).
   */
  const getActivitiesData = useCallback((): StravaActivitiesData | null => {
    const stored = localStorage.getItem(STORAGE_KEYS.ACTIVITIES_DATA);
    if (!stored) {
      return null;
    }

    try {
      const data: StravaActivitiesData = JSON.parse(stored);

      // Check if data has expired (same 6-hour expiry as preview data)
      if (Date.now() - data.fetchedAt > PREVIEW_DATA_EXPIRY_MS) {
        console.log("Strava activities data has expired, clearing");
        localStorage.removeItem(STORAGE_KEYS.ACTIVITIES_DATA);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Failed to parse Strava activities data from localStorage:", error);
      localStorage.removeItem(STORAGE_KEYS.ACTIVITIES_DATA);
      return null;
    }
  }, []);

  /**
   * Check if user has Strava activities data.
   */
  const hasActivitiesData = useCallback((): boolean => {
    return getActivitiesData() !== null;
  }, [getActivitiesData]);

  /**
   * Clear activities data from localStorage.
   * Call this after user selects an activity or leaves the selection flow.
   */
  const clearActivitiesData = useCallback((): void => {
    localStorage.removeItem(STORAGE_KEYS.ACTIVITIES_DATA);
  }, []);

  /**
   * Convert activities list response from Edge Function to storage format.
   * Maps camelCase API response to snake_case TypeScript types.
   *
   * @param response - The response from strava-exchange Edge Function (list mode)
   * @returns The activities data ready for storage
   */
  const createActivitiesDataFromResponse = useCallback(
    (response: StravaActivitiesListResponse): StravaActivitiesData => {
      // Map API response (camelCase) to storage format (snake_case)
      const mappedActivities: StravaActivitySummary[] = response.activities.map(
        (activity: StravaActivitySummaryAPI) => ({
          id: activity.id,
          name: activity.name,
          type: activity.type,
          sport_type: activity.sportType,
          start_date_local: activity.startDate,
          distance: activity.distance,
          total_elevation_gain: activity.totalElevationGain,
          moving_time: activity.movingTime,
          summary_polyline: activity.summaryPolyline,
        })
      );

      return {
        athlete: response.athlete,
        activities: mappedActivities,
        accessToken: response.accessToken,
        fetchedAt: Date.now(),
      };
    },
    []
  );

  /**
   * Get the stored access token for making authenticated Strava API requests.
   */
  const getAccessToken = useCallback((): string | null => {
    const data = getActivitiesData();
    return data?.accessToken ?? null;
  }, [getActivitiesData]);

  /**
   * Generate storage key for a specific date filter.
   */
  const getFilterStorageKey = useCallback((filter: DateFilterOption): string => {
    return `${STORAGE_KEYS.ACTIVITIES_DATA}_${filter}`;
  }, []);

  /**
   * Save activities data for a specific filter to localStorage.
   */
  const saveActivitiesDataForFilter = useCallback(
    (data: StravaActivitiesData, filter: DateFilterOption): void => {
      const key = getFilterStorageKey(filter);
      localStorage.setItem(key, JSON.stringify(data));
    },
    [getFilterStorageKey]
  );

  /**
   * Load activities data for a specific filter from localStorage.
   * Returns null if not found or expired.
   */
  const getActivitiesDataForFilter = useCallback(
    (filter: DateFilterOption): StravaActivitiesData | null => {
      const key = getFilterStorageKey(filter);
      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }

      try {
        const data: StravaActivitiesData = JSON.parse(stored);

        // Check if data has expired
        if (Date.now() - data.fetchedAt > PREVIEW_DATA_EXPIRY_MS) {
          console.log(`Strava activities data for filter ${filter} has expired, clearing`);
          localStorage.removeItem(key);
          return null;
        }

        return data;
      } catch (error) {
        console.error(`Failed to parse Strava activities data for filter ${filter}:`, error);
        localStorage.removeItem(key);
        return null;
      }
    },
    [getFilterStorageKey]
  );

  /**
   * Clear all filter-specific activity data from localStorage.
   */
  const clearAllFilteredData = useCallback((): void => {
    const filters: DateFilterOption[] = ["recent", "1month", "3months", "custom"];
    filters.forEach((filter) => {
      const key = getFilterStorageKey(filter);
      localStorage.removeItem(key);
    });
  }, [getFilterStorageKey]);

  return {
    saveActivitiesData,
    getActivitiesData,
    hasActivitiesData,
    clearActivitiesData,
    createActivitiesDataFromResponse,
    getAccessToken,
    saveActivitiesDataForFilter,
    getActivitiesDataForFilter,
    clearAllFilteredData,
  };
};
